
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import axios from 'axios';

const PAGE_SIZE = 10;

// Custom Option to show Color
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {props.data.color && (
                    <div
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: props.data.color,
                            marginRight: '8px',
                        }}
                    ></div>
                )}
                {props.label}
                {props.isSelected && <span style={{ marginLeft: 'auto' }}>✓</span>}
            </div>
        </components.Option>
    );
};

// ── Collapsing duplicate first-page fetches ───────────────────────────────────
//
// A screen can carry more than one TagSelector — the trial and licence listings
// each have an include filter and an exclude filter — and opening either asks for
// the same first page. Sharing the in-flight request turns that into one network
// call.
//
// Deliberately NOT a cache: the entry is dropped as soon as the request settles,
// so refresh() after creating a tag, and any later open, still hit the server.
// The only thing collapsed is requests that overlap in time.
const inFlightTagRequests = new Map();

function fetchTagPage(params) {
    const key = JSON.stringify(params);
    const pending = inFlightTagRequests.get(key);
    if (pending) return pending;

    const request = axios.get(`${process.env.REACT_APP_API_URL}/admin/tags`, {
        params,
        headers: {
            'access-token': localStorage.getItem('accessToken'),
        },
    });
    inFlightTagRequests.set(key, request);
    // Two handlers rather than .finally(): the promise .finally() returns would
    // reject in turn on a failed request, and nothing handles it — an unhandled
    // rejection warning. Callers handle `request` itself.
    const forget = () => inFlightTagRequests.delete(key);
    request.then(forget, forget);
    return request;
}

// ── Tag picker ────────────────────────────────────────────────────────────────
//
// Multi-select over the tag catalogue, paged from the server ten at a time and
// searched server-side.
//
// ── Nothing is fetched until the menu opens ──────────────────────────────────
//
// A listing page carries two of these and may never have either opened, so
// loading on mount spent two requests per page view on a dropdown most visits
// never touch. The list now loads on first open. Already-selected tags are
// unaffected: chips are resolved by utils/Tags/fetchTagsByIds, which looks up the
// ids in the URL directly and does not need the options list.
//
// ── Why the components and styles props are memoised ─────────────────────────
//
// react-select rebuilds its component map whenever the `components` prop changes
// identity, and a component defined inline is a NEW identity on every render —
// which unmounts and remounts the menu, resetting its scrollTop to 0. That is
// what broke paging: scrolling to the bottom fired a fetch, the fetch set state,
// the menu remounted at the top, and the newly appended tags looked like they had
// never arrived. Both objects are therefore built once, and MenuList reads
// onCreateTag through a ref so a fresh callback from the parent does not have to
// change the map's identity to be picked up.
const TagSelector = React.forwardRef(({ onChange, value, placeholder = "Select Tags...", onCreateTag }, ref) => {
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Highest page loaded for the current search term, and whether the server has
    // more after it. Refs rather than state because onMenuScrollToBottom can fire
    // twice before React re-renders, and two scroll events reading a stale `page`
    // would both request the same next page.
    const loadedPageRef = useRef(0);
    const hasMoreRef = useRef(true);
    const loadingRef = useRef(false);
    const searchRef = useRef('');

    // Search-as-you-type means responses can land out of order. Only the newest
    // request is allowed to write to state.
    const requestSeqRef = useRef(0);

    const loadPage = useCallback(async (pageToFetch, search) => {
        const seq = ++requestSeqRef.current;
        loadingRef.current = true;
        setIsLoading(true);
        try {
            const params = { limit: PAGE_SIZE, page: pageToFetch };
            if (search) {
                params['filters[0][column]'] = 'name';
                params['filters[0][operator]'] = 'LIKE';
                params['filters[0][value]'] = `%${search}%`;
            }

            const response = await fetchTagPage(params);

            // A newer search started while this was in flight — that one wins, and
            // it owns the loading flag now, so don't clear it in `finally` either.
            if (seq !== requestSeqRef.current) return;
            if (!response.data?.status) return;

            const tagsData = response.data.tags || response.data.data || [];
            const newOptions = tagsData.map(tag => ({
                value: tag.id,
                label: tag.name,
                color: tag.color,
                ...tag // spread other props just in case
            }));

            setOptions(prev => {
                if (pageToFetch === 1) return newOptions;
                const existingIds = new Set(prev.map(o => o.value));
                return [...prev, ...newOptions.filter(o => !existingIds.has(o.value))];
            });

            const total = response.data.meta?.pagination?.total ?? 0;
            loadedPageRef.current = pageToFetch;
            hasMoreRef.current = pageToFetch * PAGE_SIZE < total;
        } catch (error) {
            console.error("Failed to fetch tags", error);
        } finally {
            if (seq === requestSeqRef.current) {
                loadingRef.current = false;
                setIsLoading(false);
            }
        }
    }, []);

    // First open loads the list. Keyed off "we have no options" rather than a
    // has-opened flag so a failed first attempt retries on the next open instead
    // of leaving the dropdown permanently empty.
    const handleMenuOpen = useCallback(() => {
        if (loadingRef.current || options.length > 0) return;
        loadedPageRef.current = 0;
        hasMoreRef.current = true;
        loadPage(1, searchRef.current);
    }, [loadPage, options.length]);

    // Closing with an active search throws the filtered list away, so reopening
    // starts from the full catalogue again. Without this the input would be blank
    // while the options still held only the last search's matches.
    const handleMenuClose = useCallback(() => {
        if (!searchRef.current) return;
        searchRef.current = '';
        loadedPageRef.current = 0;
        hasMoreRef.current = true;
        setOptions([]);
    }, []);

    // ── Paging off a plain scroll listener, not react-select's onMenuScrollToBottom
    //
    // react-select only fires onMenuScrollToBottom when `captureMenuScroll` is on,
    // and that defaults to `!isTouchCapable()` — false on every touch-capable
    // browser, which includes touch-screen laptops and tablets, not just phones.
    // On those the callback never fires and paging would be silently dead. A
    // scroll listener on the menu list itself is indifferent to all of that, and
    // also covers dragging the scrollbar and keyboard paging, which a wheel-delta
    // handler misses.
    const handleMenuListScroll = useCallback((event) => {
        const el = event.currentTarget;
        // A little slack: sub-pixel heights mean scrollTop + clientHeight often
        // never quite equals scrollHeight, and firing a touch early means the next
        // page is usually there by the time the user reaches the end.
        const BOTTOM_SLACK_PX = 24;
        if (el.scrollTop + el.clientHeight < el.scrollHeight - BOTTOM_SLACK_PX) return;
        if (loadingRef.current || !hasMoreRef.current) return;
        loadPage(loadedPageRef.current + 1, searchRef.current);
    }, [loadPage]);

    const handleInputChange = useCallback((newValue, actionMeta) => {
        if (actionMeta.action !== 'input-change') return;
        searchRef.current = newValue;
        loadedPageRef.current = 0;
        hasMoreRef.current = true;
        loadPage(1, newValue);
    }, [loadPage]);

    // Read by MenuList so a new inline onCreateTag from the parent doesn't force
    // the memoised component map to be rebuilt.
    const onCreateTagRef = useRef(onCreateTag);
    onCreateTagRef.current = onCreateTag;

    const selectComponents = useMemo(() => ({
        Option,
        MenuList: (props) => (
            <components.MenuList
                {...props}
                // MenuList spreads innerProps onto its scrolling div, so this is
                // how the scroll handler gets onto the element that actually
                // scrolls. Merge rather than replace — react-select puts the
                // listbox role and id in there.
                innerProps={{ ...props.innerProps, onScroll: handleMenuListScroll }}
            >
                {props.children}
                {onCreateTagRef.current && (
                    <div
                        style={{
                            padding: '8px 12px',
                            borderTop: '1px solid #e0e0e0',
                            cursor: 'pointer',
                            fontWeight: '500',
                            textAlign: 'center'
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onCreateTagRef.current();
                        }}
                    >
                        + Add New Tag
                    </div>
                )}
            </components.MenuList>
        ),
        // handleMenuListScroll is itself stable (loadPage has no deps), so this map
        // keeps one identity for the component's whole life — which is the point.
    }), [handleMenuListScroll]);

    // Same reason as the component map: a fresh object here would churn
    // react-select's internal style resolution on every render.
    const selectStyles = useMemo(() => ({
        menuPortal: base => ({ ...base, zIndex: 9999 }), // ensure it shows on top of other things
    }), []);

    React.useImperativeHandle(ref, () => ({
        refresh: () => {
            loadedPageRef.current = 0;
            hasMoreRef.current = true;
            loadPage(1, searchRef.current);
        }
    }));

    return (
        <Select
            classNamePrefix="rs"
            isMulti
            isLoading={isLoading}
            options={options}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onInputChange={handleInputChange}
            onChange={onChange}
            value={value}
            placeholder={placeholder}
            components={selectComponents}
            menuPortalTarget={document.body}
            styles={selectStyles}
        />
    );
});

export default TagSelector;
